import { startTransition } from 'react';
import {
  isReadModelReady,
  syncEntities,
  syncExpiredProductsReadModel
} from '../../services/cacheReadModel';
import { mergeExpiredProductsForDisplay } from '../inventory/expiredView';
import {
  filterValidCustomers,
  mergeCustomersById,
  mergeSalesById,
  syncSalesCacheFromStorage
} from '../../utils/salesCacheSync';
import {
  mergePurchasesById,
  mergeProductsById,
  mergeSuppliersById,
  mergeLotsById,
  mergeEntitiesById,
  mergeCustomerDebtsById,
  normalizeLotsField
} from '../../utils/businessStateSync';
import { resolveWorkerSessionsForBusiness } from '../../utils/workerSessionsResolve';
import invoiceService from '../../services/invoiceService';

export const DEFAULT_KV_SYNC_FIELDS = [
  'sales', 'customers', 'purchases', 'products', 'lots', 'suppliers', 'stockData',
  'invoices', 'orders', 'expenses', 'invoicePayments', 'supplierDebts', 'customerDebts',
  'customerInvoices', 'supermarketPayments', 'supermarketInvoices', 'expiredProducts',
  'notifications', 'workerSessions', 'reports', 'discrepancyManagement', 'stockLocations',
  'persistentStockData', 'offlineQueue', 'businessInfo', 'userInfo', 'metadata',
  'openTickets', 'restaurantTables', 'recipes', 'productionOrders', 'stockConsumptions'
];

export async function executeBusinessFieldKvSync(bid, fields, { setters, parsedPayload }) {
  const {
    setSales, setCustomers, setPurchases, setProducts, setLots, setSuppliers, setStockData,
    setInvoices, setOrders, setExpenses, setInvoicePayments, setSupplierDebts, setCustomerDebts,
    setCustomerInvoices, setSupermarketPayments, setSupermarketInvoices, setExpiredProducts,
    setNotifications, setWorkerSessions, setReports, setDiscrepancyManagement, setStockLocations,
    setPersistentStockData, setOfflineQueue, setBusinessInfo, setUserInfo, setMetadata,
    setOpenTickets, setRestaurantTables, setRecipes, setProductionOrders, setStockConsumptions
  } = setters;

  const p = parsedPayload;
  const fieldSet = new Set(
    Array.isArray(fields) && fields.length > 0 ? fields : DEFAULT_KV_SYNC_FIELDS
  );
        const { readPurchasesFromBestSource } = await import('../../utils/purchasesReadSource');
        const { readSalesFromBestSource } = await import('../../utils/salesReadSource');
        const { readLotsFromBestSource } = await import('../../utils/lotsReadSource');
        const { readProductsFromBestSource } = await import('../../utils/productsReadSource');
        const { readCustomersFromBestSource } = await import('../../utils/customersReadSource');
        const { readSuppliersFromBestSource } = await import('../../utils/suppliersReadSource');
        const { readStockDataFromBestSource } = await import('../../utils/stockDataReadSource');
        const { readInvoicesFromBestSource } = await import('../../utils/invoicesReadSource');
        const { readOrdersFromBestSource } = await import('../../utils/ordersReadSource');
        const { readExpensesFromBestSource } = await import('../../utils/expensesReadSource');
        const { readInvoicePaymentsFromBestSource } = await import(
          '../../utils/invoicePaymentsReadSource'
        );
        const { readSupplierDebtsFromBestSource } = await import('../../utils/supplierDebtsReadSource');
        const { readCustomerDebtsFromBestSource } = await import('../../utils/customerDebtsReadSource');
        const { readCustomerInvoicesFromBestSource } = await import(
          '../../utils/customerInvoicesReadSource'
        );
        const { readSupermarketPaymentsFromBestSource } = await import(
          '../../utils/supermarketPaymentsReadSource'
        );
        const { readSupermarketInvoicesFromBestSource } = await import(
          '../../utils/supermarketInvoicesReadSource'
        );
        const { readExpiredProductsFromBestSource } = await import(
          '../../utils/expiredProductsReadSource'
        );
        const { readNotificationsFromBestSource } = await import('../../utils/notificationsReadSource');
        const { readWorkerSessionsFromBestSource } = await import(
          '../../utils/workerSessionsReadSource'
        );
        const { readReportsFromBestSource } = await import('../../utils/reportsReadSource');
        const { readDiscrepancyManagementFromBestSource } = await import(
          '../../utils/discrepancyManagementReadSource'
        );
        const { readStockLocationsFromBestSource } = await import(
          '../../utils/stockLocationsReadSource'
        );
        const { readPersistentStockDataFromBestSource } = await import(
          '../../utils/persistentStockDataReadSource'
        );
        const { readOfflineQueueFromBestSource } = await import('../../utils/offlineQueueReadSource');
        const { readBusinessInfoFromBestSource } = await import('../../utils/businessInfoReadSource');
        const { readUserInfoFromBestSource } = await import('../../utils/userInfoReadSource');
        const { readMetadataFromBestSource } = await import('../../utils/metadataReadSource');

        const [
          purchasesList,
          salesList,
          lotsList,
          productsList,
          customersList,
          suppliersList,
          stockDataDoc,
          invoicesList,
          ordersList,
          expensesList,
          invoicePaymentsList,
          supplierDebtsList,
          customerDebtsList,
          customerInvoicesList,
          supermarketPaymentsList,
          supermarketInvoicesList,
          expiredProductsList,
          notificationsList,
          workerSessionsList,
          reportsDoc,
          discrepancyManagementDoc,
          stockLocationsList,
          persistentStockDataList,
          offlineQueueList,
          businessInfoDoc,
          userInfoDoc,
          metadataDoc
        ] = await Promise.all([
          fieldSet.has('purchases') ? readPurchasesFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('sales') ? readSalesFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('lots') ? readLotsFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('products') ? readProductsFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('customers') ? readCustomersFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('suppliers')
            ? readSuppliersFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('stockData')
            ? readStockDataFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('invoices')
            ? readInvoicesFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('orders') ? readOrdersFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('expenses')
            ? readExpensesFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('invoicePayments')
            ? readInvoicePaymentsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('supplierDebts')
            ? readSupplierDebtsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('customerDebts')
            ? readCustomerDebtsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('customerInvoices')
            ? readCustomerInvoicesFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('supermarketPayments')
            ? readSupermarketPaymentsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('supermarketInvoices')
            ? readSupermarketInvoicesFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('expiredProducts')
            ? readExpiredProductsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('notifications')
            ? readNotificationsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('workerSessions')
            ? readWorkerSessionsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('reports') ? readReportsFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('discrepancyManagement')
            ? readDiscrepancyManagementFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('stockLocations')
            ? readStockLocationsFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('persistentStockData')
            ? readPersistentStockDataFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('offlineQueue')
            ? readOfflineQueueFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('businessInfo')
            ? readBusinessInfoFromBestSource(bid)
            : Promise.resolve(null),
          fieldSet.has('userInfo') ? readUserInfoFromBestSource(bid) : Promise.resolve(null),
          fieldSet.has('metadata') ? readMetadataFromBestSource(bid) : Promise.resolve(null)
        ]);

        let productsForExpiry = productsList;
        if (fieldSet.has('lots') && lotsList && !productsForExpiry) {
          productsForExpiry = await readProductsFromBestSource(bid);
        }

        startTransition(() => {
          if (fieldSet.has('sales') && salesList) {
            setSales((prev) => mergeSalesById(prev, salesList));
          }
          if (fieldSet.has('customers') && customersList) {
            setCustomers((prev) =>
              filterValidCustomers(mergeCustomersById(prev, customersList))
            );
          }
          if (fieldSet.has('purchases') && purchasesList) {
            setPurchases((prev) => mergePurchasesById(prev, purchasesList));
          }
          if (fieldSet.has('products') && productsList) {
            setProducts((prev) => mergeProductsById(prev, productsList));
          }
          if (fieldSet.has('lots') && lotsList) {
            const loadedLots = Array.isArray(lotsList)
              ? lotsList
              : normalizeLotsField(lotsList);
            setLots((prev) => {
              const prevLots = Array.isArray(prev) ? prev : normalizeLotsField(prev);
              return mergeLotsById(prevLots, loadedLots);
            });

            const prods = Array.isArray(productsForExpiry)
              ? productsForExpiry
              : Array.isArray(p.products)
                ? p.products
                : [];
            const derivedExpired = mergeExpiredProductsForDisplay([], loadedLots, prods);
            setExpiredProducts(derivedExpired);

            if (isReadModelReady()) {
              syncExpiredProductsReadModel({
                products: prods,
                lots: loadedLots,
                expiredProducts: derivedExpired
              });
            }
          }
          if (fieldSet.has('suppliers') && suppliersList !== null) {
            setSuppliers((prev) => mergeSuppliersById(prev, suppliersList));
          }
          if (fieldSet.has('stockData') && stockDataDoc) {
            setStockData((prev) => ({ ...prev, ...stockDataDoc }));
          }
          if (fieldSet.has('invoices') && invoicesList !== null) {
            setInvoices((prev) => mergeEntitiesById(prev, invoicesList, ['id', 'invoiceId']));
          }
          if (fieldSet.has('orders') && ordersList !== null) {
            setOrders((prev) => mergeEntitiesById(prev, ordersList, ['id', 'orderId']));
          }
          if (fieldSet.has('expenses') && expensesList !== null) {
            setExpenses((prev) => mergeEntitiesById(prev, expensesList, ['id']));
          }
          if (fieldSet.has('invoicePayments') && invoicePaymentsList !== null) {
            setInvoicePayments((prev) =>
              mergeEntitiesById(prev, invoicePaymentsList, ['id'])
            );
          }
          if (fieldSet.has('supplierDebts') && supplierDebtsList !== null) {
            setSupplierDebts((prev) => mergeEntitiesById(prev, supplierDebtsList, ['id']));
          }
          if (fieldSet.has('customerDebts') && customerDebtsList !== null) {
            setCustomerDebts((prev) => mergeCustomerDebtsById(prev, customerDebtsList));
          }
          if (fieldSet.has('customerInvoices') && customerInvoicesList !== null) {
            setCustomerInvoices((prev) =>
              mergeEntitiesById(prev, customerInvoicesList, ['id'])
            );
          }
          if (fieldSet.has('supermarketPayments') && supermarketPaymentsList !== null) {
            setSupermarketPayments((prev) =>
              mergeEntitiesById(prev, supermarketPaymentsList, ['id'])
            );
          }
          if (fieldSet.has('supermarketInvoices') && supermarketInvoicesList !== null) {
            setSupermarketInvoices((prev) =>
              invoiceService.mergeAndConsolidateInvoices(prev, supermarketInvoicesList, ['id'])
            );
          }
          if (fieldSet.has('expiredProducts') && expiredProductsList) {
            setExpiredProducts(
              mergeExpiredProductsForDisplay(
                expiredProductsList,
                lotsList ||
                  (Array.isArray(p.lots) ? p.lots : Object.values(p.lots || {})),
                Array.isArray(p.products) ? p.products : []
              )
            );
          }
          if (fieldSet.has('notifications') && notificationsList) {
            setNotifications(notificationsList);
          }
          if (fieldSet.has('workerSessions') && workerSessionsList) {
            setWorkerSessions(resolveWorkerSessionsForBusiness(bid, workerSessionsList));
          }
          if (fieldSet.has('reports') && reportsDoc) {
            setReports(reportsDoc);
          }
          if (fieldSet.has('discrepancyManagement') && discrepancyManagementDoc) {
            const dm = discrepancyManagementDoc;
            const normalized = {
              selfDeviations: dm.selfDeviations || [],
              handoverDeviations: dm.handoverDeviations || [],
              workerTolerances: dm.workerTolerances || {},
              pendingReviews: dm.pendingReviews || [],
              auditTrail: dm.auditTrail || [],
              notifications: dm.notifications || [],
              toleranceSystemEnabled: dm.toleranceSystemEnabled || false
            };
            setDiscrepancyManagement(normalized);
            if (isReadModelReady()) {
              syncEntities({ discrepancyManagement: normalized }, { recalc: true });
            }
          }
          if (fieldSet.has('stockLocations') && stockLocationsList) {
            setStockLocations(stockLocationsList);
          }
          if (fieldSet.has('persistentStockData') && persistentStockDataList) {
            setPersistentStockData(persistentStockDataList);
          }
          if (fieldSet.has('offlineQueue') && offlineQueueList) {
            setOfflineQueue(offlineQueueList);
          }
          if (fieldSet.has('businessInfo')) {
            setBusinessInfo(businessInfoDoc);
          }
          if (fieldSet.has('userInfo')) {
            setUserInfo(userInfoDoc);
          }
          if (fieldSet.has('metadata') && metadataDoc) {
            setMetadata(metadataDoc);
          }
          // Shell restaurant ops — only replace when payload actually has the array.
          // Never coerce missing → [] (wipes Kasse/Cuisine after send under NO_KV_BLOB).
          if (
            fieldSet.has('openTickets') &&
            typeof setOpenTickets === 'function' &&
            Array.isArray(p.openTickets)
          ) {
            setOpenTickets(p.openTickets);
          }
          if (
            fieldSet.has('restaurantTables') &&
            typeof setRestaurantTables === 'function' &&
            Array.isArray(p.restaurantTables)
          ) {
            setRestaurantTables(p.restaurantTables);
          }
          if (
            fieldSet.has('recipes') &&
            typeof setRecipes === 'function' &&
            Array.isArray(p.recipes)
          ) {
            setRecipes((prev) => {
              const prevArr = Array.isArray(prev) ? prev : [];
              if (p.recipes.length === 0) return prevArr;
              if (prevArr.length === 0) return p.recipes;
              return mergeEntitiesById(prevArr, p.recipes, ['id']);
            });
          }
          if (
            fieldSet.has('productionOrders') &&
            typeof setProductionOrders === 'function' &&
            Array.isArray(p.productionOrders)
          ) {
            setProductionOrders((prev) => {
              const prevArr = Array.isArray(prev) ? prev : [];
              if (p.productionOrders.length === 0) return prevArr;
              if (prevArr.length === 0) return p.productionOrders;
              return mergeEntitiesById(prevArr, p.productionOrders, ['id']);
            });
          }
          if (
            fieldSet.has('stockConsumptions') &&
            typeof setStockConsumptions === 'function' &&
            Array.isArray(p.stockConsumptions)
          ) {
            setStockConsumptions((prev) => {
              const prevArr = Array.isArray(prev) ? prev : [];
              if (p.stockConsumptions.length === 0) return prevArr;
              if (prevArr.length === 0) return p.stockConsumptions;
              return mergeEntitiesById(prevArr, p.stockConsumptions, ['id']);
            });
          }
        });

        if (fieldSet.has('sales')) {
          syncSalesCacheFromStorage(bid);
        }
        if (isReadModelReady()) {
          const partial = {};
          if (fieldSet.has('sales') && salesList) partial.sales = salesList;
          if (fieldSet.has('customers')) partial.customers = p.customers || [];
          if (fieldSet.has('purchases') && purchasesList) {
            partial.purchases = purchasesList;
          }
          if (fieldSet.has('products')) partial.products = p.products || [];
          if (fieldSet.has('lots') && lotsList) partial.lots = lotsList;
          if (fieldSet.has('suppliers') && suppliersList) partial.suppliers = suppliersList;
          if (fieldSet.has('stockData') && stockDataDoc) partial.stockData = stockDataDoc;
          if (fieldSet.has('invoices') && invoicesList) partial.invoices = invoicesList;
          if (fieldSet.has('orders') && ordersList) partial.orders = ordersList;
          if (fieldSet.has('expenses') && expensesList) partial.expenses = expensesList;
          if (fieldSet.has('invoicePayments') && invoicePaymentsList) {
            partial.invoicePayments = invoicePaymentsList;
          }
          if (fieldSet.has('supplierDebts') && supplierDebtsList) {
            partial.supplierDebts = supplierDebtsList;
          }
          if (fieldSet.has('customerDebts') && customerDebtsList) {
            partial.customerDebts = customerDebtsList;
          }
          if (fieldSet.has('customerInvoices') && customerInvoicesList) {
            partial.customerInvoices = customerInvoicesList;
          }
          if (fieldSet.has('supermarketPayments') && supermarketPaymentsList) {
            partial.supermarketPayments = supermarketPaymentsList;
          }
          if (fieldSet.has('supermarketInvoices') && supermarketInvoicesList) {
            partial.supermarketInvoices = supermarketInvoicesList;
          }
          if (fieldSet.has('reports') && reportsDoc) partial.reports = reportsDoc;
          if (fieldSet.has('discrepancyManagement') && discrepancyManagementDoc) {
            partial.discrepancyManagement = discrepancyManagementDoc;
          }
          if (fieldSet.has('stockLocations') && stockLocationsList) {
            partial.stockLocations = stockLocationsList;
          }
          if (Object.keys(partial).length > 0) {
            syncEntities(partial, { recalc: true });
          }
        }
}

